import { useEffect, useState } from 'react';
import { Users2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../auth/AuthContext';
import {
  useIntervenants,
  listUserOptions,
  ajouterIntervenant,
  retirerIntervenant,
  ROLES_DOSSIER,
  type RoleDossier,
  type UserOption,
} from '../intervenants';
import { useConfirm } from './ConfirmDialog';

interface Props {
  prestNumero: string;
}

export default function IntervenantsPanel({ prestNumero }: Props) {
  const { hasRole } = useAuth();
  const confirm = useConfirm();
  const canEdit = hasRole('admin') || hasRole('direction');
  const { items, loading, prestUuid, reload } = useIntervenants(prestNumero);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [pickUser, setPickUser] = useState('');
  const [pickRole, setPickRole] = useState<RoleDossier>('commercial');

  useEffect(() => { listUserOptions().then(setUsers); }, []);

  const handleAdd = async () => {
    if (!prestUuid || !pickUser) { toast.error('Sélectionne un utilisateur'); return; }
    const err = await ajouterIntervenant(prestUuid, pickUser, pickRole);
    if (err) {
      if (err.code === '23505') toast.error('Cet utilisateur a déjà ce rôle sur le dossier');
      else toast.error(err.message);
    } else {
      toast.success('Intervenant ajouté');
      setPickUser('');
      reload();
    }
  };

  const handleRemove = async (id: string) => {
    const ok = await confirm({
      title: 'Retirer cet intervenant ?',
      description: 'Il ne sera plus assigné à ce dossier.',
      confirmLabel: 'Retirer',
      destructive: true,
    });
    if (!ok) return;
    const err = await retirerIntervenant(id);
    if (err) toast.error(err.message);
    else { toast.success('Retiré'); reload(); }
  };

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-200">
        <Users2 size={15} strokeWidth={1.5} className="text-amber-400" />
        Intervenants sur le dossier
        <span className="text-xs text-slate-500 font-normal">({items.length})</span>
      </div>

      {loading ? (
        <div className="text-xs text-slate-500">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-slate-500 italic">Aucun intervenant assigné.</div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((it) => {
            const role = ROLES_DOSSIER.find((r) => r.value === it.role_dossier);
            return (
              <li key={it.id} className="flex items-center gap-2 text-sm bg-slate-950/50 border border-slate-800 rounded-lg px-3 py-2">
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${role?.color || 'bg-slate-700/30 text-slate-300 border-slate-600/40'}`}>
                  {role?.label || it.role_dossier}
                </span>
                <span className="flex-1 truncate text-slate-200">
                  {it.user_display_name || it.user_email || it.user_id.slice(0, 8)}
                  {it.user_display_name && it.user_email && (
                    <span className="text-slate-500 text-xs ml-2">{it.user_email}</span>
                  )}
                </span>
                {canEdit && (
                  <button
                    onClick={() => handleRemove(it.id)}
                    className="text-slate-500 hover:text-red-400 transition-colors"
                    title="Retirer"
                  >
                    <X size={14} strokeWidth={1.5} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {canEdit ? (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-800">
          <select
            value={pickUser}
            onChange={(e) => setPickUser(e.target.value)}
            className="flex-1 min-w-[180px] px-2 py-1.5 bg-slate-950 border border-slate-800 rounded-md text-xs text-slate-200"
          >
            <option value="">— Choisir un utilisateur —</option>
            {users.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.display_name || u.email}
              </option>
            ))}
          </select>
          <select
            value={pickRole}
            onChange={(e) => setPickRole(e.target.value as RoleDossier)}
            className="px-2 py-1.5 bg-slate-950 border border-slate-800 rounded-md text-xs text-slate-200"
          >
            {ROLES_DOSSIER.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <button
            onClick={handleAdd}
            disabled={!prestUuid || !pickUser}
            className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-950 text-xs font-medium rounded-md flex items-center gap-1.5 transition-colors"
          >
            <Plus size={12} strokeWidth={2} /> Ajouter
          </button>
        </div>
      ) : (
        <div className="text-[11px] text-slate-500 italic pt-2 border-t border-slate-800">
          Seuls les administrateurs et la direction peuvent assigner des intervenants.
        </div>
      )}
    </div>
  );
}
